import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom'
import { readBack } from '../../../lib/backNav'
import JSZip from 'jszip'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import type { Devanning, DevanningItem, DeliveryNote, DeliveryNoteType } from '../types'
import { fmtDate, fmtDateTime } from '../types'
import type { DeliveryCollectionNoteData } from './DeliveryCollectionNotePDF'
import { NoteWysiwygEditor } from './NoteWysiwygEditor'
import {
  blankNoteDraft, noteDraftFromNote, resolveNote, buildNoteData, buildNoteRow, type NoteDraft, type NoteAuto,
} from '../../../lib/deliveryNoteDraft'
import { generateNotePdf } from '../../../lib/deliveryNoteSave'
import type { PickedAddress } from './AddressPicker'
import { getCompanySettings, type CompanySettings } from '../../../lib/companySettings'
import { resolveBoxtopEmployeeName, downloadBlob } from '../../../lib/transportNote'
import DispatchStatusBadge from '../../../components/DispatchStatusBadge'
import { dispatchProgress, scaleForQuantity } from '../../../lib/dispatchProgress'
import { methodCodeForNoteKind, summariseDescriptions, type DeliveryMethodRow } from '../../../lib/fulfilmentType'

const STATUS_BADGE: Record<DeliveryNote['status'], string> = {
  ACTIVE:     'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  SUPERSEDED: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  VOID:       'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}
const LIFECYCLE_BADGE: Record<DeliveryNote['lifecycle_status'], string> = {
  DRAFT:     'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  PLANNED:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  COMPLETED: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
}

// A "group" is the physical devan split staff actually recorded cartons/pallets against --
// see Report.tsx's buildRows. The underlying loading-list line items it was built from
// never carry their own actual figures.
interface ItemGroup {
  key: string
  itemIds: string[]
  hblNos: string[]
  clientRefSuffix: string | null // e.g. "01" from "KI20284M-01", for the sub-ref note text
  descriptions: (string | null)[]
  cartons: number
  pallets: number
  weightKg: number | null
  cbm: number | null
  // Raw devanning_items.delivery_method free text for this group -- feeds the fulfilment-type
  // suggestion (see lib/fulfilmentType.ts). Not deduped here; suggestFulfilmentType does that.
  deliveryMethods: (string | null)[]
  // Clean-path counterpart -- set when an item was picked from the pricing_delivery_methods
  // dropdown (DeliveryMethodSelect) rather than free text.
  deliveryMethodIds: (string | null)[]
}

function groupItems(items: DevanningItem[]): ItemGroup[] {
  const map = new Map<string, DevanningItem[]>()
  for (const it of items) {
    const key = it.group_name || it.split_no || it.id
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(it)
  }
  return Array.from(map.entries()).map(([key, its]) => {
    const first = its[0]
    const skipDetail = first?.skip_pallet_detail ?? true
    const actualCartons = skipDetail ? (first?.manual_carton_sum ?? 0) : (first?.actual_cartons_received ?? 0)
    const expectedCartons = its.reduce((s, i) => s + (i.expected_cartons || 0), 0)
    const pallets = skipDetail
      ? (first?.manual_pallet_total ?? 0)
      : (first?.pallets_detail || []).filter(p => p.ctns > 0).length
    const clientRef = first?.plan_item?.client_ref || null
    return {
      key,
      itemIds: its.map(i => i.id),
      hblNos: [...new Set(its.map(i => i.hbl_no).filter(Boolean))] as string[],
      clientRefSuffix: clientRef?.includes('-') ? clientRef.split('-').pop()! : null,
      descriptions: its.map(i => i.description),
      cartons: actualCartons || expectedCartons,
      pallets,
      // actual_* first (nothing writes it yet, so today this is always the loading-list figure),
      // per item so a half-filled group still sums correctly. Null -- not 0 -- when neither
      // exists so the cell shows as Empty for staff to fill in.
      weightKg: its.reduce((s, i) => s + (i.actual_weight_kg || i.expected_weight_kg || 0), 0) || null,
      cbm: its.reduce((s, i) => s + (i.actual_cbm || i.expected_cbm || 0), 0) || null,
      deliveryMethods: its.map(i => i.delivery_method),
      deliveryMethodIds: its.map(i => i.delivery_method_id),
    }
  })
}

function sumGroups(groups: ItemGroup[], sourceKey: string | null) {
  const sel = groups.filter(g => g.key === sourceKey)
  return {
    cartons: sel.reduce((s, g) => s + g.cartons, 0),
    pallets: sel.reduce((s, g) => s + g.pallets, 0),
    weightKg: sel.reduce((s, g) => s + (g.weightKg || 0), 0) || null,
    cbm: sel.reduce((s, g) => s + (g.cbm || 0), 0) || null,
    hblNos: [...new Set(sel.flatMap(g => g.hblNos))],
    itemIds: sel.flatMap(g => g.itemIds),
    subRefs: sel.map(g => g.clientRefSuffix).filter(Boolean) as string[],
    descriptions: sel.flatMap(g => g.descriptions),
    deliveryMethods: sel.flatMap(g => g.deliveryMethods),
    deliveryMethodIds: sel.flatMap(g => g.deliveryMethodIds),
  }
}

// Map a stored note back to the group it was raised against (source_devanning_item_id first,
// item_ids overlap for notes made before that column existed).
function keyForNote(groups: ItemGroup[], note: DeliveryNote): string | null {
  const src = note.source_devanning_item_id
  const hit = src
    ? groups.find(g => g.itemIds.includes(src))
    : groups.find(g => (note.item_ids || []).some(id => g.itemIds.includes(id)))
  return hit?.key ?? null
}

function warehouseAddressLines(addr: unknown): string[] {
  if (!addr || typeof addr !== 'object') return []
  const a = addr as Record<string, string>
  return [a.line1, a.line2, a.city, a.postcode].filter(Boolean)
}

// Sea: "Container: {no} - SEAL: {seal} - {type} -". Air: "AWB: {no}" / "MAWB: {no}".
// A trailing " - " after the last present field matches the verified sample's exact
// punctuation (docs/warehouse/samples/COLLECTION_NOTE_SAMPLE.pdf).
function buildRemarks(opts: {
  mode: 'sea' | 'air'
  containerNo?: string | null
  sealNo?: string | null
  containerType?: string | null
  awbNo?: string | null
  mawbNo?: string | null
}): string {
  if (opts.mode === 'air') {
    if (opts.mawbNo) return `MAWB: ${opts.mawbNo}`
    if (opts.awbNo) return `AWB: ${opts.awbNo}`
    return ''
  }
  if (!opts.containerNo) return ''
  const parts = [`Container: ${opts.containerNo}`]
  if (opts.sealNo) parts.push(`SEAL: ${opts.sealNo}`)
  if (opts.containerType) parts.push(opts.containerType)
  return parts.join(' - ') + (opts.sealNo || opts.containerType ? ' -' : '')
}

// Z44: the draft helpers live in lib/deliveryNoteDraft.ts, shared with the Job -> Transport tab.
const blankDraft = blankNoteDraft
function draftFromNote(note: DeliveryNote, groups: ItemGroup[]): NoteDraft {
  return noteDraftFromNote(note, keyForNote(groups, note))
}

export default function DeliveryNotesPage() {
  const { id, did } = useParams<{ id: string; did: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  // ?back=&label= -- set by whichever page linked here (Inbound row, Planned Outbound...), so
  // every way in has a way back to exactly where staff came from.
  const back = readBack(searchParams)
  // ?group=<devanning group key> -- list only the notes of one row; ?note=<id> -- open that note.
  const groupFilter = searchParams.get('group')
  // Arrived straight at one row's editor/viewer (Inbound drawer -> New Note / Open): closing it
  // goes back to where staff came from instead of dropping them on this container-level page.
  const directEntry = !!back && !!(searchParams.get('item') || searchParams.get('note'))
  const noteParam = searchParams.get('note')
  // ACTIVE PLANNED/COMPLETED notes open read-only (PDF preview + Download/Reissue/Void).
  const [viewing, setViewing] = useState<DeliveryNote | null>(null)
  function closeViewing() { setViewing(null); if (directEntry && back) navigate(back.path) }
  const { user, userEmail } = useAuth()

  const [devanning, setDevanning] = useState<Devanning | null>(null)
  const [items, setItems] = useState<DevanningItem[]>([])
  const [notes, setNotes] = useState<DeliveryNote[]>([])
  const [company, setCompany] = useState<CompanySettings | null>(null)
  // The devanning's own warehouse, as a ready-to-use address -- the "smart default" for
  // Delivery's Release To / Collection's Collect From (see AddressPicker.tsx).
  const [autoWarehouseAddress, setAutoWarehouseAddress] = useState<PickedAddress | null>(null)
  const [issuerName, setIssuerName] = useState<string>('')
  // Z44: the job this devanning's notes belong to (matched by job ref), so the note also shows on that job's Transport tab. null = no job has this ref.
  const [linkedJobId, setLinkedJobId] = useState<string | null>(null)
  const [methods, setMethods] = useState<DeliveryMethodRow[]>([])
  const [loading, setLoading] = useState(true)
  const [genError, setGenError] = useState<string | null>(null)
  const [zipping, setZipping] = useState(false)
  const [busyNoteId, setBusyNoteId] = useState<string | null>(null)
  // VOID and SUPERSEDED notes are collapsed by default (a devanning accumulates a lot of these
  // over reissues/mistakes) -- one shared toggle for both sections, persisted so staff don't
  // have to re-expand it every visit.
  const [showHidden, setShowHidden] = useState<boolean>(() => {
    try { return localStorage.getItem('notes.showHiddenStatuses') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('notes.showHiddenStatuses', showHidden ? '1' : '0') } catch { /* ignore */ }
  }, [showHidden])

  const [draft, setDraft] = useState<NoteDraft | null>(null)
  const [draftDbId, setDraftDbId] = useState<string | null>(null)
  const [draftSerial, setDraftSerial] = useState<number | null>(null)
  const [draftPdfUrl, setDraftPdfUrl] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [draftBusy, setDraftBusy] = useState<'generate' | 'plan' | null>(null)
  const preselectedRef = useRef<string | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const insertInFlightRef = useRef(false)
  const draftDbIdRef = useRef<string | null>(null)
  function setDraftDbIdBoth(v: string | null) { draftDbIdRef.current = v; setDraftDbId(v) }
  // Tracks whether the user has EXPLICITLY chosen to keep this draft (Save Draft / Generate
  // PDF / Push to Planned) -- as opposed to the 500ms autosave, which fires passively on any
  // edit just so work-in-progress survives a refresh. Closing (X or Cancel) without ever
  // taking an explicit action should discard the autosaved row, not leave an orphan behind.
  const draftExplicitlySavedRef = useRef(false)

  const groups = groupItems(items)
  const isHiddenStatus = (n: DeliveryNote) => n.status === 'VOID' || n.status === 'SUPERSEDED'
  const visibleNotes = (showHidden ? notes : notes.filter(n => !isHiddenStatus(n)))
    .filter(n => !groupFilter || keyForNote(groups, n) === groupFilter)
  const hiddenCount = notes.filter(isHiddenStatus).length

  const fetchAll = useCallback(async () => {
    if (!did) return
    const [{ data: dev }, { data: its }, { data: ns }] = await Promise.all([
      supabase.from('devannings')
        .select('*, plan:devanning_plans(container_no, vessel, job_ref, eta_date, planned_devan_at, source_ll:loading_lists(job_ref))')
        .eq('id', did).single(),
      supabase.from('devanning_items').select('*, plan_item:devanning_plan_items(client_ref)').eq('devanning_id', did).order('group_name').order('id'),
      supabase.from('delivery_notes').select('*').eq('devanning_id', did).order('created_at'),
    ])
    setDevanning(dev as Devanning)
    setItems((its as DevanningItem[]) || [])
    setNotes((ns as DeliveryNote[]) || [])
    setLoading(false)
  }, [did])

  useEffect(() => { fetchAll() }, [fetchAll])
  useEffect(() => {
    getCompanySettings().then(setCompany)
    resolveBoxtopEmployeeName(userEmail).then(setIssuerName)
    // pricing_delivery_methods.default_fulfilment_type -- the clean path suggestFulfilmentType
    // prefers over the free-text keyword heuristic. Small lookup table, fetched once.
    supabase.from('pricing_delivery_methods').select('id, code, name, default_fulfilment_type').eq('is_active', true).order('name').then(({ data }) => {
      setMethods((data as DeliveryMethodRow[] | null) || [])
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (!devanning?.warehouse_id) return
    supabase.from('warehouses').select('id, code, name, address').eq('id', devanning.warehouse_id).maybeSingle().then(({ data }) => {
      const w = data as { id: string; code: string; name: string; address: unknown } | null
      if (!w) { setAutoWarehouseAddress(null); return }
      setAutoWarehouseAddress({ name: w.name, lines: warehouseAddressLines(w.address), phone: null, customerId: null, warehouseId: w.id })
    })
  }, [devanning?.warehouse_id])

  // Deep-linked from Inbound's per-row "Collection Note" button (?item=<devanning_item_id>)
  // -- that button only navigates here when no ACTIVE Collection Note already covers the
  // item, so open a fresh draft with the item's group pre-checked once items have loaded.
  useEffect(() => {
    if (!items.length || preselectedRef.current === did) return
    const preselectItemId = searchParams.get('item')
    if (!preselectItemId) return
    preselectedRef.current = did || null
    const gs = groupItems(items)
    const group = gs.find(g => g.itemIds.includes(preselectItemId))
    if (group) openNewForGroup(group.key)
  }, [items, did, searchParams])

  // Deep-linked ?note=<id> (from an Inbound row's note list or another page's Source link).
  const openedNoteRef = useRef<string | null>(null)
  useEffect(() => {
    if (!noteParam || openedNoteRef.current === noteParam || !items.length || !notes.length) return
    const n = notes.find(x => x.id === noteParam)
    if (!n) return
    openedNoteRef.current = noteParam
    openNote(n)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteParam, items, notes])

  const jobRef = (devanning?.plan as any)?.source_ll?.job_ref ?? devanning?.plan?.job_ref ?? devanning?.container_no ?? ''
  useEffect(() => {
    if (!jobRef) { setLinkedJobId(null); return }
    supabase.from('jobs').select('id').eq('job_ref', jobRef).maybeSingle().then(({ data }) => setLinkedJobId((data as { id: string } | null)?.id ?? null))
  }, [jobRef])

  // The auto-computed default for each overridable field -- the WYSIWYG editor's sidebar
  // compares these against the draft's *Override state to decide Auto vs Overridden vs Empty.
  // Cartons already covered by OTHER generated, ACTIVE notes for the same source row -- excludes
  // the note being edited/reissued, whose own quantity is being replaced.
  function dispatchedByOthers(d: NoteDraft): number {
    const g = groups.find(x => x.key === d.sourceKey)
    if (!g) return 0
    const others = notes.filter(n => n.id !== d.reissueOf && n.id !== draftDbIdRef.current)
    return dispatchProgress(g.cartons, others, g.itemIds).dispatched
  }

  function autoValues(d: NoteDraft) {
    const totals = sumGroups(groups, d.sourceKey)
    const remainingCtns = Math.max(totals.cartons - dispatchedByOthers(d), 0)
    // Default quantity = what's still left on the row; weight/cube/pallets follow it pro rata
    // (all untouched when the note takes the whole row). Every one stays individually overridable.
    const nopNow = d.nopOverride ?? remainingCtns
    const weightKg = scaleForQuantity(totals.weightKg, nopNow, totals.cartons, 'none', 1)
    const cbm = scaleForQuantity(totals.cbm, nopNow, totals.cartons, 'none', 2)
    const pallets = scaleForQuantity(totals.pallets, nopNow, totals.cartons, 'ceil') ?? 0
    const remarks = buildRemarks({ mode: 'sea', containerNo: devanning?.container_no, sealNo: null, containerType: null })
    // "I authorise you to release these goods to: X" -- X is always whoever Release To names
    // (courier/customer/supplier/own driver), same meaning regardless of fulfilment type.
    const authoriseTo = d.releaseTo.name
    const description = summariseDescriptions(totals.descriptions)
    // Summary column = pallet count ("15 PLTS"); empty (not "0 PLTS") when none recorded yet.
    const summary = pallets > 0 ? `${pallets} PLTS` : ''
    return { nop: remainingCtns, weightKg, cbm, remarks, authoriseTo, description, summary, totals: { ...totals, pallets }, remainingCtns, alreadyDispatched: dispatchedByOthers(d) }
  }

  // Z44: what a note contains (PDF data, the delivery_notes row, which method / kind / fulfilment it resolves to) is decided by the shared
  // builders in lib/deliveryNoteDraft.ts -- the same ones the Job -> Transport tab uses. This page only supplies its devanning-derived values.
  function noteAuto(d: NoteDraft): NoteAuto {
    const av = autoValues(d)
    return { nop: av.nop, weightKg: av.weightKg, cbm: av.cbm, remarks: av.remarks, authoriseTo: av.authoriseTo,
      description: av.description, summary: av.summary, pallets: av.totals.pallets, itemIds: av.totals.itemIds }
  }
  const resolved = (d: NoteDraft) => resolveNote(d, methods, groups)
  const resolvedNoteType = (d: NoteDraft): DeliveryNoteType => resolved(d).noteType
  const resolvedFulfilment = (d: NoteDraft) => resolved(d).fulfilment

  // `issuedAt` is a param, not computed here -- the caller (generatePdfFor) freezes it once at the actual moment of generating, and
  // writes that exact same value to the DB row, so the PDF and the stored issued_at can never drift apart.
  function buildDraftNoteData(d: NoteDraft, serialNo: number | null, issuedAt: string, readinessStatus: DeliveryNote['readiness_status'] = null): DeliveryCollectionNoteData {
    return buildNoteData({ draft: d, auto: noteAuto(d), resolved: resolved(d), jobRef, company: company!, serialNo, issuerName, issuedAt, readinessStatus })
  }

  // The delivery_notes row for the current draft -- shared by autosave, Save Draft, Generate PDF and Push to Planned Outbound.
  function buildRow(d: NoteDraft) {
    return buildNoteRow({
      draft: d, auto: noteAuto(d), resolved: resolved(d), jobRef, devanningId: did ?? null, jobId: linkedJobId,
      issuer: { userId: user?.id || null, name: issuerName },
      previousNote: notes.find(n => n.id === d.reissueOf) ?? null,
    })
  }

  // Debounced (500ms) autosave -- first edit on a brand-new/reissue draft INSERTs (assigning
  // its serial_no from the DB sequence), every edit after that UPDATEs the same row. Nothing
  // is marked SUPERSEDED here -- that only happens when the reissue is actually finalised
  // (Generate PDF / Push to Planned), so an abandoned draft never silently replaces the note
  // it was reissuing.
  function scheduleSave(d: NoteDraft, existingDbId: string | null) {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      // Guards against a rapid second edit's own debounce firing before the first
      // insert (which assigns the row's id/serial_no) has come back -- without this,
      // both would see existingDbId still null and insert two rows for one draft.
      if (!existingDbId && insertInFlightRef.current) { scheduleSave(d, existingDbId); return }
      setSaving(true)
      try {
        const row = buildRow(d)
        if (existingDbId) {
          const { error } = await supabase.from('delivery_notes').update(row).eq('id', existingDbId)
          if (error) throw error
        } else {
          insertInFlightRef.current = true
          const { data, error } = await supabase.from('delivery_notes').insert(row).select().single()
          if (error) throw error
          const saved = data as DeliveryNote
          setDraftDbIdBoth(saved.id)
          setDraftSerial(saved.serial_no)
        }
      } catch (err: any) {
        setGenError(err.message || 'Autosave failed')
      } finally {
        setSaving(false)
        insertInFlightRef.current = false
      }
    }, 500)
  }

  // A note always starts from one source row (an Inbound row = one devanning group): its
  // quantities come along automatically, staff only edit NOP for a partial shipment.
  function openNewForGroup(key: string) {
    const g = groups.find(x => x.key === key)
    const suggestedNotes = g?.clientRefSuffix ? `${jobRef}-${g.clientRefSuffix}` : ''
    setDraft(blankDraft('COLLECTION_COURIER', key, suggestedNotes, autoWarehouseAddress))
    setDraftDbIdBoth(null); setDraftSerial(null); setDraftPdfUrl(null)
    draftExplicitlySavedRef.current = false
    setGenError(null)
  }
  function draftForNote(note: DeliveryNote): NoteDraft {
    const d = draftFromNote(note, groups)
    if (!d.deliveryMethodId) {
      // Saved before delivery_method_id existed: map note_type back to its method.
      const code = methodCodeForNoteKind(note.note_type)
      d.deliveryMethodId = methods.find(m => m.code === code)?.id ?? null
    }
    return d
  }
  function openReissue(note: DeliveryNote) {
    setViewing(null)
    setDraft(draftForNote(note))
    setDraftDbIdBoth(null); setDraftSerial(null); setDraftPdfUrl(null)
    draftExplicitlySavedRef.current = false
    setGenError(null)
  }
  // Edit a saved DRAFT in place (same editor, same row -- autosave UPDATEs it). Unlike a reissue
  // this doesn't create a new note. Marked explicitly-saved so closing without further edits
  // doesn't delete the row, which already existed before this session.
  function openDraftExisting(note: DeliveryNote) {
    setViewing(null)
    setDraft({ ...draftForNote(note), reissueOf: null })
    setDraftDbIdBoth(note.id); setDraftSerial(note.serial_no); setDraftPdfUrl(note.pdf_url)
    draftExplicitlySavedRef.current = true
    setGenError(null)
  }
  // The one entry point for "open this existing note" -- DRAFT edits, anything else previews.
  function openNote(note: DeliveryNote) {
    if (note.status === 'ACTIVE' && note.lifecycle_status === 'DRAFT') openDraftExisting(note)
    else setViewing(note)
  }
  function updateDraft(patch: Partial<NoteDraft>) {
    setDraft(d => {
      if (!d) return d
      const next = { ...d, ...patch }
      scheduleSave(next, draftDbIdRef.current)
      return next
    })
  }
  async function closeDraft() {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    // The 500ms autosave may have already inserted a row from a passing edit even though the
    // user never explicitly chose to keep this draft -- discard it so X/Cancel behaves like a
    // real cancel instead of silently leaving an orphan DRAFT note behind.
    const idToDiscard = draftDbIdRef.current
    if (idToDiscard && !draftExplicitlySavedRef.current) {
      await supabase.from('delivery_notes').delete().eq('id', idToDiscard).eq('lifecycle_status', 'DRAFT')
    }
    setDraft(null)
    if (directEntry && back) { navigate(back.path); return }
    fetchAll()
  }

  async function flushSave(d: NoteDraft): Promise<DeliveryNote> {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    // If an autosave insert is still in flight (assigning the row's id/serial_no), wait
    // for it rather than racing it with a second insert for the same draft.
    for (let i = 0; i < 50 && insertInFlightRef.current; i++) await new Promise(r => setTimeout(r, 100))
    const row = buildRow(d)
    // Read the ref, not the `draftDbId` state this closure captured at call time -- the
    // wait above may have let a pending autosave finish and assign the row's real id.
    const currentDbId = draftDbIdRef.current
    if (currentDbId) {
      const { data, error } = await supabase.from('delivery_notes').update(row).eq('id', currentDbId).select().single()
      if (error) throw error
      return data as DeliveryNote
    }
    const { data, error } = await supabase.from('delivery_notes').insert(row).select().single()
    if (error) throw error
    const saved = data as DeliveryNote
    setDraftDbIdBoth(saved.id); setDraftSerial(saved.serial_no)
    return saved
  }

  async function generatePdfFor(saved: DeliveryNote, d: NoteDraft, readinessStatus: DeliveryNote['readiness_status'] = null): Promise<string> {
    // Frozen here, at the actual moment of generating -- not draft-creation time. Reissue goes through this same path with a fresh row, so it
    // gets a fresh issuedAt too, with no separate handling needed.
    const issuedAt = new Date().toISOString()
    return generateNotePdf({
      saved, data: buildDraftNoteData(d, saved.serial_no, issuedAt, readinessStatus),
      folder: String(jobRef || devanning?.container_no), filePrefix: resolvedNoteType(d), issuedAt, userId: user?.id || null, issuerName,
    })
  }

  async function finalizeSupersede(d: NoteDraft, newId: string) {
    if (!d.reissueOf) return
    await supabase.from('delivery_notes')
      .update({ status: 'SUPERSEDED', superseded_by: newId })
      .eq('id', d.reissueOf).eq('status', 'ACTIVE')
  }

  async function saveDraftNow() {
    if (!draft) return
    setSaving(true)
    try {
      await flushSave(draft)
      draftExplicitlySavedRef.current = true
    } catch (err: any) {
      setGenError(err.message || 'Save failed')
    } finally { setSaving(false) }
  }

  // Guards the fulfilment-type suggestion staying an unresolved null (ambiguous/no keyword
  // match/multiple methods) from ever silently reaching the DB -- Gary's spec requires a
  // forced manual pick in that case, not a null falling through.
  function blockOnUnresolvedFulfilment(): boolean {
    if (draft && resolvedFulfilment(draft).type === null) {
      setGenError('Fulfilment type could not be auto-suggested for this note, please choose it manually above (Collection Only / Collection + Delivery).')
      return true
    }
    return false
  }

  async function generatePdf() {
    if (!company || !devanning || !draft) return
    if (blockOnUnresolvedFulfilment()) return
    setDraftBusy('generate'); setGenError(null)
    try {
      const saved = await flushSave(draft)
      const url = await generatePdfFor(saved, draft)
      setDraftPdfUrl(url)
      await finalizeSupersede(draft, saved.id)
      draftExplicitlySavedRef.current = true
      window.open(url, '_blank')
    } catch (err: any) {
      setGenError(err.message || 'Failed to generate PDF')
    } finally { setDraftBusy(null) }
  }

  // Z50.X readiness check -- staff can legitimately push a note to Planned Outbound before
  // the container has even arrived (booking transport ahead of time), but that state has to
  // be marked so it's never confused with "goods are in the warehouse, ready to go" on the
  // Planned Outbound page.
  function computeReadiness(): NonNullable<DeliveryNote['readiness_status']> | null {
    if (!devanning) return null
    if (devanning.status === 'awaiting' || devanning.status === 'started') {
      const label = devanning.status === 'awaiting' ? 'Awaiting' : 'Started'
      const ok = window.confirm(
        `⚠️ This container hasn't finished devanning yet (status: ${label}). Pushing will mark this note as PENDING ARRIVAL -- come back to confirm once it's arrived and devanned. Continue?`
      )
      return ok ? 'PENDING_ARRIVAL' : null
    }
    if (!devanning.pushed_to_inbound) {
      const ok = window.confirm(
        '⚠️ This container has been devanned but not yet inbounded. Pushing will mark this note as PENDING INBOUND. Continue?'
      )
      return ok ? 'PENDING_INBOUND' : null
    }
    return 'READY'
  }

  async function pushToPlanned() {
    if (!company || !devanning || !draft) return
    if (blockOnUnresolvedFulfilment()) return
    const readinessStatus = computeReadiness()
    if (!readinessStatus) return // user cancelled the confirm, or declined
    setDraftBusy('plan'); setGenError(null)
    try {
      const saved = await flushSave(draft)
      // Always regenerate (not reused from an earlier Generate PDF click) so the watermark
      // reflects the readiness just computed above, not a stale pre-push render.
      const url = await generatePdfFor(saved, draft, readinessStatus)
      await supabase.from('delivery_notes').update({
        lifecycle_status: 'PLANNED', planned_at: new Date().toISOString(), planned_by: user?.id || null,
        readiness_status: readinessStatus, readiness_checked_at: new Date().toISOString(),
        // Only a Collection+Delivery job tracks a transit leg at all -- confirming this note
        // collected (PlannedOutbound's submitComplete) flips this same row straight to
        // IN_TRANSIT, which is what makes it show up on the Deliveries board.
        transit_status: saved.fulfilment_type === 'COLLECTION_AND_DELIVERY' ? 'PENDING' : null,
      }).eq('id', saved.id)
      await finalizeSupersede(draft, saved.id)
      setDraftPdfUrl(url)
      draftExplicitlySavedRef.current = true
      closeDraft()
    } catch (err: any) {
      setGenError(err.message || 'Failed to push to Planned Outbound')
    } finally { setDraftBusy(null) }
  }

  async function voidNote(note: DeliveryNote) {
    const reason = window.prompt(`Void ${note.note_type} #${note.serial_no}? Optional reason:`)
    if (reason === null) return
    setBusyNoteId(note.id)
    try {
      await supabase.from('delivery_notes').update({
        status: 'VOID', voided_at: new Date().toISOString(), voided_by: user?.id || null,
        void_reason: reason || null,
      }).eq('id', note.id)
      await fetchAll()
    } finally { setBusyNoteId(null) }
  }

  async function downloadAllZip() {
    setZipping(true)
    try {
      const zip = new JSZip()
      const entries = notes.filter(n => n.status === 'ACTIVE' && n.pdf_url).map(n => ({ name: `${n.note_type}-${n.serial_no}.pdf`, url: n.pdf_url! }))
      if (!entries.length) { setZipping(false); return }
      for (const e of entries) {
        const res = await fetch(e.url)
        zip.file(e.name, await res.blob())
      }
      const blob = await zip.generateAsync({ type: 'blob' })
      downloadBlob(blob, `${jobRef || devanning?.container_no || 'notes'}_notes.zip`)
    } finally { setZipping(false) }
  }

  function NoteList({ list }: { list: DeliveryNote[] }) {
    if (!list.length) {
      return <div className="text-center py-6 text-gray-400 text-sm">No Notes generated yet.</div>
    }
    // Full `notes` (not just this list) -- a superseded note's replacement can, in principle,
    // be filtered out of `list` by showHidden even though it can't actually be VOID/SUPERSEDED
    // itself (finalizeSupersede only ever points at the newly-created ACTIVE note).
    const bySerial = (id: string | null) => id ? notes.find(x => x.id === id)?.serial_no : null
    return (
      <div className="space-y-2">
        {list.map(n => (
          <div key={n.id} id={`note-${n.id}`} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${n.status === 'VOID' ? 'border-red-200 dark:border-red-800' : 'border-gray-200 dark:border-gray-700'}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold font-mono">#{n.serial_no}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_BADGE[n.status]}`}>{n.status}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${LIFECYCLE_BADGE[n.lifecycle_status]}`}>{n.lifecycle_status}</span>
                <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {n.note_type === 'COLLECTION_SELF' ? 'Self-Pickup' : n.note_type === 'COLLECTION_SUPPLIER' ? 'Supplier-Pickup' : 'Courier'}
                </span>
                {n.fulfilment_type && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${n.fulfilment_type === 'COLLECTION_AND_DELIVERY' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'}`}>
                    {n.fulfilment_type === 'COLLECTION_AND_DELIVERY' ? 'Collection + Delivery' : 'Collection Only'}
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  {n.release_to_name || '—'} · {fmtDate(n.scheduled_date)} · {n.nop} ctns
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                Issued by {n.issued_by_name || 'unknown'} · {fmtDateTime(n.issued_at)}
                {n.status === 'SUPERSEDED' && n.superseded_by && (
                  <> · <button onClick={() => document.getElementById(`note-${n.superseded_by}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    className="underline hover:text-blue-500">Superseded by {bySerial(n.superseded_by) ? `#${bySerial(n.superseded_by)}` : 'newer version'}</button></>
                )}
                {n.status === 'VOID' && ` · voided ${fmtDateTime(n.voided_at)}${n.void_reason ? `: ${n.void_reason}` : ''}`}
                {n.fulfilment_type === 'COLLECTION_AND_DELIVERY' && !n.deliver_to_name && (
                  <> · <span className="text-amber-600 dark:text-amber-400">⚠️ Needs Deliver To address</span></>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => openNote(n)}
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">
                {n.status === 'ACTIVE' && n.lifecycle_status === 'DRAFT' ? 'Edit' : 'Open'}
              </button>
              {n.pdf_url && (
                <button onClick={() => window.open(n.pdf_url!, '_blank')}
                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 font-medium">PDF</button>
              )}
              {n.status === 'ACTIVE' && (
                <>
                  <button onClick={() => openReissue(n)}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 font-medium">Reissue</button>
                  <button onClick={() => voidNote(n)} disabled={busyNoteId === n.id}
                    className="px-2 py-1 text-xs text-red-500 hover:underline disabled:opacity-50">Void</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const activeCount = notes.filter(n => n.status === 'ACTIVE').length
  const voidCount = notes.filter(n => n.status === 'VOID').length
  const supersededCount = notes.filter(n => n.status === 'SUPERSEDED').length
  const hiddenSummaryParts = [
    voidCount ? `${voidCount} voided` : null,
    supersededCount ? `${supersededCount} superseded` : null,
  ].filter(Boolean)
  const hasDownloadableNote = notes.some(n => n.status === 'ACTIVE' && n.pdf_url)

  function HiddenToggle({ count }: { count: number }) {
    if (!count) return null
    return (
      <button onClick={() => setShowHidden(s => !s)} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline">
        {showHidden ? 'Hide voided/superseded' : `Show voided/superseded (${count})`}
      </button>
    )
  }

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {back
            ? <Link to={back.path} className="text-sm text-blue-600 hover:underline">← Back to {back.label}</Link>
            : <Link to={`/warehouse/${id}/devannings/${did}/report`} className="text-sm text-blue-600 hover:underline">← Report</Link>}
          {back && <Link to={`/warehouse/${id}/devannings/${did}/report`} className="text-xs text-gray-400 hover:underline">Report</Link>}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Release/Delivery Notes — {devanning?.container_no}</h2>
            <p className="text-sm text-gray-500">
              {jobRef} · {activeCount} Note{activeCount !== 1 ? 's' : ''}
              {hiddenSummaryParts.length > 0 && <span className="text-gray-400"> ({hiddenSummaryParts.join(', ')})</span>}
            </p>
          </div>
          <button onClick={downloadAllZip} disabled={zipping || !hasDownloadableNote}
            title={!hasDownloadableNote ? 'No active notes to download' : undefined}
            className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            {zipping ? 'Zipping…' : 'Download All (ZIP)'}
          </button>
        </div>
        {genError && <p className="text-red-600 text-sm">{genError}</p>}

        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Rows to dispatch</h3>
              <p className="text-xs text-gray-400 mb-3">Each row is one destination group. A note takes the row's remaining quantity; change NOP on the note for a partial shipment and raise another note for the rest.</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-1.5 pr-3">Group</th><th className="py-1.5 pr-3 text-right">Ctns</th>
                    <th className="py-1.5 pr-3 text-right">Dispatched</th><th className="py-1.5 pr-3">Status</th><th />
                  </tr>
                </thead>
                <tbody>
                  {groups.map(g => {
                    const prog = dispatchProgress(g.cartons, notes, g.itemIds)
                    return (
                      <tr key={g.key} className="border-b border-gray-100 dark:border-gray-700/50">
                        <td className="py-1.5 pr-3 text-gray-800 dark:text-gray-200">{g.key}{g.clientRefSuffix ? ` (${g.clientRefSuffix})` : ''}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">{g.cartons}</td>
                        <td className="py-1.5 pr-3 text-right font-mono">{prog.dispatched}</td>
                        <td className="py-1.5 pr-3"><DispatchStatusBadge progress={prog} /></td>
                        <td className="py-1.5 text-right">
                          <button onClick={() => openNewForGroup(g.key)} className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-lg hover:bg-green-200">
                            + New Note
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 dark:text-white">Notes</h3>
                <div className="flex items-center gap-3">
                  <HiddenToggle count={hiddenCount} />
                </div>
              </div>
              {groupFilter && (
                <div className="mb-3 flex items-center gap-2 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg px-3 py-1.5">
                  Showing notes for row <strong>{jobRef} · {groupFilter}</strong>
                  <button onClick={() => { const p = new URLSearchParams(searchParams); p.delete('group'); navigate({ search: p.toString() }, { replace: true }) }}
                    className="underline">Show all notes</button>
                </div>
              )}
              <NoteList list={visibleNotes} />
            </div>

            {viewing && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 md:p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col">
                  <div className="px-4 md:px-6 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
                    <div>
                      {back && (
                        <button onClick={() => { setViewing(null); navigate(back.path) }}
                          className="text-xs text-blue-600 hover:underline mb-0.5">← Back to {back.label}</button>
                      )}
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
                        Note #{viewing.serial_no}
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_BADGE[viewing.status]}`}>{viewing.status}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${LIFECYCLE_BADGE[viewing.lifecycle_status]}`}>{viewing.lifecycle_status}</span>
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {viewing.release_to_name || '—'} · {fmtDate(viewing.scheduled_date)} · {viewing.nop} ctns · {viewing.pallets_qty} plts
                        {viewing.weight_kg != null ? ` · ${viewing.weight_kg} kg` : ''}{viewing.cube_cbm != null ? ` · ${viewing.cube_cbm} cbm` : ''}
                      </p>
                    </div>
                    <button onClick={closeViewing} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                  </div>
                  <div className="flex-1 min-h-[300px] bg-gray-100 dark:bg-gray-900">
                    {viewing.pdf_url
                      ? <iframe src={viewing.pdf_url} title={`Note #${viewing.serial_no}`} className="w-full h-[65vh] border-0" />
                      : <p className="p-8 text-center text-sm text-gray-400">No PDF has been generated for this note yet.</p>}
                  </div>
                  <div className="px-4 md:px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-3 justify-end">
                    {viewing.pdf_url && <a href={viewing.pdf_url} target="_blank" rel="noreferrer" download className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50">Download PDF</a>}
                    {viewing.status === 'ACTIVE' && (
                      <>
                        <button onClick={() => openReissue(viewing)} className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50">Reissue</button>
                        <button onClick={async () => { await voidNote(viewing); closeViewing() }} disabled={busyNoteId === viewing.id}
                          className="px-4 py-2 text-sm font-medium border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50">Void</button>
                      </>
                    )}
                    <button onClick={closeViewing} className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50">Close</button>
                  </div>
                </div>
              </div>
            )}

            {draft && company && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 md:p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white dark:bg-gray-800 px-4 md:px-6 pt-4 md:pt-5 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between z-10">
                    <div>
                      {back && (
                        <button onClick={async () => { await closeDraft(); navigate(back.path) }}
                          className="text-xs text-blue-600 hover:underline mb-0.5">← Back to {back.label}</button>
                      )}
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {draft.reissueOf ? 'Reissue' : draftDbId && !draft.reissueOf && draftExplicitlySavedRef.current ? 'Edit' : 'New'} Release/Delivery Note
                      </h3>
                      <p className="text-xs text-gray-400">{saving ? 'Saving…' : draftDbId ? `Draft saved${draftSerial ? ` — #${draftSerial}` : ''}` : 'Not saved yet — edit a field to autosave'}</p>
                    </div>
                    <button onClick={closeDraft} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                  </div>
                  <div className="px-4 md:px-6 py-4">
                    <NoteWysiwygEditor
                      draft={draft} onChange={updateDraft} groups={groups}
                      autoNop={autoValues(draft).remainingCtns} dispatchedElsewhere={autoValues(draft).alreadyDispatched} autoWeightKg={autoValues(draft).weightKg} autoCbm={autoValues(draft).cbm}
                      autoRemarks={autoValues(draft).remarks} autoAuthoriseTo={autoValues(draft).authoriseTo}
                      autoDescription={autoValues(draft).description} autoSummary={autoValues(draft).summary}
                      devanning={devanning} company={company} autoWarehouseAddress={autoWarehouseAddress} jobRef={jobRef}
                      serialNo={draftSerial} issuerName={issuerName} methods={methods}
                    />
                    {(() => {
                      const av = autoValues(draft)
                      const nopNow = draft.nopOverride ?? av.nop
                      const total = av.totals.cartons
                      if (av.alreadyDispatched + nopNow <= total) return null
                      return (
                        <p className="text-xs text-red-600 mt-2">⚠ {av.alreadyDispatched} of {total} ctns already went out on other notes — this note ({nopNow}) brings the row to {av.alreadyDispatched + nopNow}/{total}.</p>
                      )
                    })()}
                  </div>
                  <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-4 md:px-6 pb-4 md:pb-5 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-3 justify-end">
                    {draftPdfUrl && <a href={draftPdfUrl} download className="px-4 py-2 text-sm text-blue-600 hover:underline self-center mr-auto">Download PDF Preview</a>}
                    <button onClick={closeDraft} className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    <button onClick={saveDraftNow} disabled={saving} className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                      Save Draft
                    </button>
                    <button onClick={generatePdf} disabled={draftBusy !== null || !draft.sourceKey}
                      className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      {draftBusy === 'generate' ? 'Generating…' : 'Generate PDF'}
                    </button>
                    <button onClick={pushToPlanned} disabled={draftBusy !== null || !draft.sourceKey}
                      className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                      {draftBusy === 'plan' ? 'Pushing…' : 'Push to Planned Outbound'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </WarehouseLayout>
  )
}
